import {useRouter} from 'next/router';
import NextLink from 'next/link';
import {Box, BoxProps, Flex, Heading, Image, Text} from '@chakra-ui/react';
import {useIntl} from 'react-intl';
import dayjs from 'dayjs';

import {LikeIcon} from '@eksab/assets/icons';
import {getCountry} from '@eksab/util';

import {ArticleBadges} from './';
import {getLeagues, stripParagraphs} from '../helpers';
import type {Article} from '../types';

interface ArticleCardProps extends BoxProps {
  article: Article;
}

export const FeaturedArticleCard = ({article, ...props}: ArticleCardProps) => {
  const {locale} = useRouter();
  const format = useIntl().formatNumber;

  const leagues = getLeagues(article);

  return (
    <NextLink href={`/news/${article.slug}`} locale={`ar-${getCountry(locale)}`} passHref legacyBehavior>
      <Box
        as="a"
        pos="relative"
        display="flex"
        flexDir={['column-reverse', null, 'row']}
        bg="gray.800"
        rounded="md"
        shadow="xs"
        overflow="hidden"
        h={['auto', null, '23.75rem']}
        {...props}
      >
        <Box display="flex" flexDir="column" px="5" pt={[2.5, null, 8]} pb="4" flexBasis={['initial', null, '50%']}>
          <Text fontSize="sm" fontWeight="700" color="green.400" mb={leagues ? [1, null, 5] : 0}>
            {leagues}
          </Text>

          <Heading
            fontSize="clamp(1.3rem, 5vw, 2rem)"
            color="white"
            noOfLines={[2, null, 3]}
            lineHeight={[1.8, null, 1.5]}
            mb="6"
          >
            {article.title}
          </Heading>

          <Box display={['none', null, 'block']}>
            <Text
              fontSize="md"
              fontWeight="500"
              color="white"
              noOfLines={leagues ? 2 : 3}
              dangerouslySetInnerHTML={{__html: stripParagraphs(article.body).substring(0, 300)}}
            />
          </Box>

          <Flex align="flex-end" justify="space-between" flex={1}>
            <Text fontSize="sm" color="white">
              {dayjs().to(article.published_at)}
            </Text>

            {!!article.likes && (
              <Text display="flex" alignItems="center" gap="1.5" fontSize="sm" color="white">
                {format(article.likes)}
                <LikeIcon />
              </Text>
            )}
          </Flex>
        </Box>

        <Box flexBasis={['65%', null, '50%']}>
          <Image
            w="full"
            h={['auto', null, 'full']}
            maxH={[96, null, 'unset']}
            objectFit="cover"
            src={article.image?.original}
            alt={article.title}
          />
        </Box>

        <ArticleBadges top="5" sponsor={article.sponsor} />
      </Box>
    </NextLink>
  );
};
